import { mapDefaults } from "@/info/map-defaults";
import type { Filters } from "@/types/filters";
import { logger } from "@/utils/logger";
import { useCallback } from "react";

interface UseDataFetchOrchestratorParams {
	mode: "default" | "project";
	fetchMapData: (params: { filters: Partial<Filters>; append?: boolean }) => Promise<void>;
	fetchNearByProjects: (params: { filters: Partial<Filters> }) => Promise<void>;
}

interface OrchestrateParams {
	filters: Partial<Filters>;
	map?: google.maps.Map | null;
	filtersForGraphQL: Partial<Filters>;
	isInitialFetch?: boolean; // Flag to indicate first post-SSR fetch
}

export function useDataFetchOrchestrator({ mode, fetchMapData, fetchNearByProjects }: UseDataFetchOrchestratorParams) {
	const orchestrateFetch = useCallback(
		async ({ filters, map, filtersForGraphQL, isInitialFetch = false }: OrchestrateParams) => {
			if (mode === "project") {
				const shouldForceZoom = !!filters.selectedLocationId;

				// Case 1: Auto-zoom to location (only if location is selected)
				if (shouldForceZoom && map) {
					// After setZoom(projectZoom), wait for the map to be idle before reading bounds
					return new Promise<void>((resolve, reject) => {
						let timeoutId: NodeJS.Timeout | null = null;
						let listener: google.maps.MapsEventListener | null = null;

						const cleanup = () => {
							if (listener) {
								listener.remove();
								listener = null;
							}
							if (timeoutId) {
								clearTimeout(timeoutId);
								timeoutId = null;
							}
						};

						const performFetch = () => {
							const mapBounds = map.getBounds();
							if (!mapBounds) {
								fetchMapData({ filters: filtersForGraphQL })
									.then(() => resolve())
									.catch(reject);
								return;
							}

							const boundsData = {
								north: mapBounds.getNorthEast().lat(),
								south: mapBounds.getSouthWest().lat(),
								east: mapBounds.getNorthEast().lng(),
								west: mapBounds.getSouthWest().lng(),
							};

							const nearbyProjectsFilters: Partial<Filters> = {
								...filters,
								bounds: [boundsData],
							};
							const { bounds: _omit, ...filtersForGraphQLWithoutBounds } = filtersForGraphQL;

							fetchMapData({ filters: filtersForGraphQLWithoutBounds })
								.then(() => fetchNearByProjects({ filters: nearbyProjectsFilters }))
								.then(() => {
									resolve();
								})
								.catch(error => {
									reject(error);
								});
						};

						// Set zoom first (only if not already at target zoom)
						// Note: setZoom might trigger idle immediately if change is small/instant
						const currentZoom = map.getZoom();
						if (currentZoom !== mapDefaults.projectZoom) {
							map.setZoom(mapDefaults.projectZoom);

							/* eslint-disable prefer-const */
							let zoomListener: google.maps.MapsEventListener | null = null;

							// Extend cleanup to remove zoom stuff
							const originalCleanup = cleanup;
							// redefine cleanup in scope
							const enhancedCleanup = () => {
								originalCleanup();
								if (zoomListener) {
									zoomListener.remove();
									zoomListener = null;
								}
							};

							const checkZoom = () => {
								if (Math.round(map.getZoom() || 0) === mapDefaults.projectZoom) {
									// Target zoom reached! Now wait for bounds to settle.
									// The 'idle' listener will handle the fetch, so we can clean up the zoom listener.
									if (zoomListener) {
										zoomListener.remove();
										zoomListener = null;
									}
								}
							};

							zoomListener = map.addListener("zoom_changed", checkZoom);

							// Keep idle listener as backup/race winner
							listener = map.addListener("idle", () => {
								enhancedCleanup();
								performFetch();
							});

							// Safety timeout
							timeoutId = setTimeout(() => {
								enhancedCleanup();
								logger.warn("Map idle timeout reached, proceeding with data fetch");
								performFetch();
							}, 5000);
						} else {
							// Already at target zoom, fetch immediately
							performFetch();
						}
					});
				} else {
					// Case 2: General Browsing in Project Mode (No auto-zoom)
					// Just fetch based on current map state
					if (map) {
						const mapBounds = map.getBounds();
						if (mapBounds) {
							const boundsData = {
								north: mapBounds.getNorthEast().lat(),
								south: mapBounds.getSouthWest().lat(),
								east: mapBounds.getNorthEast().lng(),
								west: mapBounds.getSouthWest().lng(),
							};

							const nearbyProjectsFilters: Partial<Filters> = {
								...filters,
								bounds: [boundsData],
							};
							const { bounds: _omit, ...filtersForGraphQLWithoutBounds } = filtersForGraphQL;

							return fetchMapData({ filters: filtersForGraphQLWithoutBounds }).then(() =>
								fetchNearByProjects({ filters: nearbyProjectsFilters }),
							);
						}
					}

					// Fallback if no map bounds available (should be rare)
					return fetchMapData({ filters: filtersForGraphQL });
				}
			} else {
				// Use append: true ONLY for the first post-SSR fetch with bounds-only filter
				const filterKeys = Object.keys(filtersForGraphQL);
				const hasOnlyBoundsFilter = filterKeys.length === 1 && filterKeys[0] === "bounds" && !!filtersForGraphQL.bounds;
				const shouldAppend = isInitialFetch && hasOnlyBoundsFilter;

				return fetchMapData({
					filters: filtersForGraphQL,
					append: shouldAppend,
				}).catch(fetchError => {
					logger.graphqlError(fetchError, "MAP_DATA_FETCH_FILTERS", filtersForGraphQL);
					throw fetchError;
				});
			}
		},
		[mode, fetchMapData, fetchNearByProjects],
	);

	return { orchestrateFetch };
}
